import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Wand2, FolderOpen, User, DollarSign, BookOpen, FileText, BarChart3, ShieldCheck, ScrollText, Scale } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

const Sidebar = ({ isOpen, onClose, onProfileClick }) => {
  const location = useLocation();
  const { userProfile } = useAuth();
  const isAdmin = userProfile?.role === 'admin';
  const [isDocsDropdownOpen, setIsDocsDropdownOpen] = useState(false);

  // Main navigation items
  const mainNavItems = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'Generate', path: '/generate', icon: Wand2 },
    { name: 'Blog', path: '/blog', icon: BookOpen },
  ];

  // Documentation dropdown items
  const docsDropdownItems = [
    { name: 'Guides', path: '/guides', icon: FileText },
    { name: 'Privacy', path: '/privacy', icon: ShieldCheck },
    { name: 'Terms', path: '/terms', icon: ScrollText },
    { name: 'License', path: '/license', icon: Scale },
  ];

  // Bottom navigation items (icon only)
  const bottomNavItems = [
    { name: 'Pricing', path: '/pricing', icon: DollarSign },
    { name: 'Docs', isDropdown: true, icon: BookOpen },
    { name: 'Profile', path: '/profile', icon: User, requiresAuth: true, hasGradient: true },
  ];

  // Add admin link if user is admin (at the bottom)
  if (isAdmin) {
    bottomNavItems.push({ name: 'Admin', path: '/admin', icon: BarChart3, specialColor: 'text-orange-400' });
  }

  const handleNavClick = (item) => {
    // Close sidebar on mobile/tablet
    if (window.innerWidth < 640) {
      onClose?.();
    }
    
    // Handle profile click when not logged in
    if (item.requiresAuth && onProfileClick) {
      onProfileClick(item);
    }
  };

  const renderNavItem = (item, iconOnly = false) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    
    // Handle dropdown items
    if (item.isDropdown) {
      return (
        <div key="docs-dropdown" className="relative">
          <div
            onClick={() => setIsDocsDropdownOpen(!isDocsDropdownOpen)}
            className={cn(
              'w-14 h-14 flex flex-col items-center justify-center rounded-xl transition-all duration-200 group cursor-pointer',
              docsDropdownItems.some(dropdownItem => dropdownItem.path === location.pathname)
                ? 'bg-white/20 text-white shadow-lg'
                : 'text-cyan-100 hover:bg-white/10 hover:text-white'
            )}
            data-testid="sidebar-docs-dropdown"
          >
            <Icon className={cn(
              'w-5 h-5',
              docsDropdownItems.some(dropdownItem => dropdownItem.path === location.pathname) ? 'text-yellow-400' : ''
            )} />
          </div>
          
          {/* Dropdown Menu */}
          {isDocsDropdownOpen && (
            <>
              {/* Backdrop to close dropdown */}
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setIsDocsDropdownOpen(false)}
              />
              
              {/* Dropdown content */}
              <div className="absolute left-24 bottom-0 ml-2 bg-white rounded-lg shadow-xl border border-gray-200 py-2 min-w-[180px] z-50">
                {docsDropdownItems.map((dropdownItem) => {
                  const DropdownIcon = dropdownItem.icon;
                  const isDropdownActive = location.pathname === dropdownItem.path;
                  return (
                    <Link
                      key={dropdownItem.path}
                      to={dropdownItem.path}
                      onClick={() => {
                        setIsDocsDropdownOpen(false);
                        handleNavClick(dropdownItem);
                      }}
                      className={cn(
                        'flex items-center gap-3 px-4 py-2 transition-colors',
                        isDropdownActive
                          ? 'bg-cyan-50 text-cyan-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      )}
                      data-testid={`dropdown-${dropdownItem.name.toLowerCase()}-link`}
                    >
                      <DropdownIcon className="w-4 h-4" />
                      <span className="text-sm font-medium">{dropdownItem.name}</span>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </div>
      );
    }
    
    const content = (
      <div
        className={cn(
          'flex flex-col items-center justify-center rounded-xl transition-all duration-200 group cursor-pointer',
          iconOnly 
            ? 'w-14 h-14' 
            : 'w-20 h-20',
          isActive
            ? 'bg-white/20 text-white shadow-lg'
            : 'text-cyan-100 hover:bg-white/10 hover:text-white'
        )}
      >
        {item.hasGradient && !isActive ? (
          <div className="relative">
            <Icon className={cn(
              iconOnly ? 'w-5 h-5' : 'w-7 h-7',
              iconOnly ? '' : 'mb-1',
              'text-cyan-300 drop-shadow-[0_0_8px_rgba(34,211,238,0.6)]'
            )} />
          </div>
        ) : (
          <Icon className={cn(
            iconOnly ? 'w-5 h-5' : 'w-7 h-7',
            iconOnly ? '' : 'mb-1',
            isActive ? 'text-yellow-400' : item.specialColor || ''
          )} />
        )}
        {!iconOnly && <span className="text-xs font-medium text-center leading-tight">{item.name}</span>}
      </div>
    );

    if (item.requiresAuth) {
      return (
        <div
          key={item.path}
          onClick={() => handleNavClick(item)}
          data-testid={`sidebar-${item.name.toLowerCase()}-link`}
        >
          {content}
        </div>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => handleNavClick(item)}
        data-testid={`sidebar-${item.name.toLowerCase()}-link`}
      >
        {content}
      </Link>
    );
  };

  return (
    <>
      {/* Overlay for very small screens only */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 sm:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 h-screen w-24 bg-gradient-to-b from-cyan-700 to-turquoise-700 text-white shadow-xl z-50 transition-transform duration-300 flex flex-col',
          // Hide only on very small screens (< 640px)
          isOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'
        )}
      >
        {/* Logo */}
        <Link
          to="/"
          onClick={() => handleNavClick({})}
          className="flex flex-col items-center justify-center py-4 border-b border-cyan-600 group flex-shrink-0 bg-gradient-to-br from-turquoise-50 via-cyan-50 to-turquoise-100"
        >
          <img 
            src="/fluxara-logo.png" 
            alt="Fluxara Logo" 
            className="w-16 h-16 object-contain transition-transform group-hover:scale-105"
          />
        </Link>

        {/* Main Navigation - Scrollable */}
        <nav className="py-4 flex flex-col items-center space-y-2 flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-cyan-600 scrollbar-track-transparent">
          {mainNavItems.map(item => renderNavItem(item, false))}
        </nav>

        {/* Bottom Navigation - Icon Only, Fixed at Bottom */}
        <nav className="py-2 flex flex-col items-center space-y-1 border-t border-cyan-600 flex-shrink-0">
          {bottomNavItems.map(item => renderNavItem(item, true))}
        </nav>

        {/* Footer */}
        <div className="py-3 text-center border-t border-cyan-600 flex-shrink-0">
          <p className="text-[9px] text-cyan-200">© 2025</p>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;